import React, { useState, useEffect } from 'react';
import {
  Container,
  Button,
  Table,
  Card,
  Form,
  Alert,
  Badge,
  Spinner,
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

function CollaboratorManagement({ token }) {
  const [collaborators, setCollaborators] = useState([]);
  const [newCollaborator, setNewCollaborator] = useState('');
  const [permission, setPermission] = useState('push');
  const [loading, setLoading] = useState(true);
  const [addLoading, setAddLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { repoName } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchCollaborators();
  }, [repoName, token]);

  const fetchCollaborators = async () => {
    try {
      console.log('Fetching collaborators for:', repoName);
      const response = await axios.get(
        `${BACKEND_URL}/api/repositories/${repoName}/collaborators`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );
      console.log('Collaborators response:', response.data);
      setCollaborators(
        Array.isArray(response.data) ? response.data : []
      );
    } catch (error) {
      console.error('Error fetching collaborators:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        setError(
          error.response?.data?.error ||
            'Failed to fetch collaborators'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddCollaborator = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setAddLoading(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/repositories/${repoName}/collaborators`,
        {
          username: newCollaborator,
          permission,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );

      setSuccess(
        `Collaborator ${newCollaborator} added successfully!`
      );
      setNewCollaborator('');
      await fetchCollaborators();
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        setError(
          error.response?.data?.error || 'Failed to add collaborator'
        );
      }
      console.error('Add collaborator error:', error);
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveCollaborator = async (username, type) => {
    setError(null);
    setSuccess(null);
    setActionLoadingId(username);

    try {
      await axios.delete(
        `${BACKEND_URL}/api/repositories/${repoName}/collaborators/${username}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );

      const message =
        type === 'pending'
          ? `Invitation for ${username} cancelled successfully!`
          : `Collaborator ${username} removed successfully!`;

      setSuccess(message);
      await fetchCollaborators();
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        const action =
          type === 'pending'
            ? 'cancel invitation'
            : 'remove collaborator';
        setError(
          error.response?.data?.error || `Failed to ${action}`
        );
      }
      console.error('Remove/cancel error:', error);
    } finally {
      setActionLoadingId(null);
    }
  };

  const getPermissionBadge = (permissions) => {
    if (permissions.admin) return <Badge bg="danger">Admin</Badge>;
    if (permissions.push) return <Badge bg="success">Write</Badge>;
    if (permissions.pull) return <Badge bg="secondary">Read</Badge>;
    return <Badge bg="light">Unknown</Badge>;
  };

  const getStatusBadge = (type) => {
    return type === 'pending' ? (
      <Badge bg="warning">Pending</Badge>
    ) : (
      <Badge bg="success">Active</Badge>
    );
  };

  return (
    <Container>
      <Card className="mt-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h2>Collaborators for {repoName}</h2>
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Back to Repositories
          </Button>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          <Form onSubmit={handleAddCollaborator} className="mb-4">
            <Form.Group className="d-flex">
              <Form.Control
                type="text"
                placeholder="GitHub Username"
                value={newCollaborator}
                onChange={(e) => setNewCollaborator(e.target.value)}
                required
                className="me-2"
                disabled={addLoading}
              />
              <Form.Select
                value={permission}
                onChange={(e) => setPermission(e.target.value)}
                className="me-2"
                style={{ width: '200px' }}
                disabled={addLoading}
              >
                <option value="pull">Read Access</option>
                <option value="push">Write Access</option>
                <option value="admin">Admin Access</option>
              </Form.Select>
              <Button
                type="submit"
                variant="primary"
                disabled={addLoading}
              >
                {addLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Adding...
                  </>
                ) : (
                  'Add Collaborator'
                )}
              </Button>
            </Form.Group>
          </Form>

          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">
                  Loading collaborators...
                </span>
              </Spinner>
              <p className="mt-2">Loading collaborators...</p>
            </div>
          ) : collaborators.length === 0 ? (
            <Alert variant="info">No collaborators found.</Alert>
          ) : (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Status</th>
                  <th>Permissions</th>
                  <th>Details</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {collaborators.map((collab) => (
                  <tr key={collab.login}>
                    <td className="align-middle">
                      <div className="d-flex align-items-center">
                        <img
                          src={collab.avatar_url}
                          alt={collab.login}
                          style={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '50%',
                            marginRight: '10px',
                          }}
                        />
                        {collab.login}
                      </div>
                    </td>
                    <td className="align-middle">
                      {getStatusBadge(collab.type)}
                    </td>
                    <td className="align-middle">
                      {getPermissionBadge(collab.permissions)}
                    </td>
                    <td className="align-middle">
                      {collab.type === 'pending' && (
                        <>
                          Invited by: {collab.invited_by}
                          <br />
                          {collab.invited_at &&
                            `Invited: ${format(
                              new Date(collab.invited_at),
                              'PPp'
                            )}`}
                        </>
                      )}
                    </td>
                    <td className="align-middle">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() =>
                          handleRemoveCollaborator(
                            collab.login,
                            collab.type
                          )
                        }
                        disabled={actionLoadingId === collab.login}
                      >
                        {actionLoadingId === collab.login ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-1"
                            />
                            {collab.type === 'pending'
                              ? 'Cancelling...'
                              : 'Removing...'}
                          </>
                        ) : collab.type === 'pending' ? (
                          'Cancel Invitation'
                        ) : (
                          'Remove'
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default CollaboratorManagement;
